import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet'
import CreditScore from '@mui/icons-material/CreditScore'
import People from '@mui/icons-material/People'

export default function WalletSummary({ summary, onRefresh }) {
  if (!summary) {
    return (
      <Typography color="text.secondary">No wallet data. You may need to complete company setup.</Typography>
    )
  }

  const {
    totalCredits = 0,
    usedCredits = 0,
    balance = 0,
    currency = 'INR',
    companyName,
    enabled,
    walletUsedPercentage,
    walletServicePolicy,
    walletReminderState,
  } = summary
  const policy = summary.creditUsagePolicy || {}
  const currentUsage = summary.currentUserUsage || {}
  const enabledServices = walletServicePolicy?.enabledServices ?? []
  const isEventBookingEnabled = enabledServices.includes('event-booking')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary">
        {companyName}
        {!enabled && ' • Wallet disabled'}
        {enabled && isEventBookingEnabled && ' • Company Credits (Event Booking)'}
      </Typography>
      {walletUsedPercentage != null && (
        <Typography variant="body2" color="text.secondary">
          Usage: {walletUsedPercentage}% of total credits
          {walletReminderState?.threshold75Sent && ' • 75% reminder sent'}
          {walletReminderState?.threshold85Sent && ' • 85% reminder sent'}
          {walletReminderState?.threshold95Sent && ' • 95% reminder sent'}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Card sx={{ minWidth: 180, flex: '1 1 160px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalanceWallet color="primary" />
              <Typography color="text.secondary" variant="body2">Balance</Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold">{balance.toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">{currency} credits</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, flex: '1 1 160px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CreditScore color="primary" />
              <Typography color="text.secondary" variant="body2">Total / Used</Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold">{usedCredits.toLocaleString()} / {totalCredits.toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">{currency}</Typography>
          </CardContent>
        </Card>
      </Box>

      {currentUsage.userId && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <People color="primary" />
              <Typography fontWeight="600">Your usage (this window)</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Monthly: {currentUsage.monthlyUsedCredits ?? 0} / {currentUsage.monthlyCap ?? 0} used
              {currentUsage.monthlyRemainingCredits != null && ` (${currentUsage.monthlyRemainingCredits} left)`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall: {currentUsage.overallUsedCredits ?? 0} / {currentUsage.overallCap ?? 0} used
              {currentUsage.overallRemainingCredits != null && ` (${currentUsage.overallRemainingCredits} left)`}
            </Typography>
            {currentUsage.monthlyWindowStart && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Window: {new Date(currentUsage.monthlyWindowStart).toLocaleDateString()} – {new Date(currentUsage.monthlyWindowEnd).toLocaleDateString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {policy.policyType && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Credit policy</Typography>
            <Typography variant="body2">
              Type: {policy.policyType}
              {policy.monthlyUserCreditCap != null && ` • Monthly cap: ${policy.monthlyUserCreditCap}`}
              {policy.overallUserCreditCap != null && ` • Overall cap: ${policy.overallUserCreditCap}`}
              {policy.allowNominatedRelatives && ` • Relatives: ${policy.maxNominatedRelativesPerEmployee ?? 0} per employee`}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
